/**
 * "Put this on your phone" — shown once to each of client, worker and admin.
 *
 * The offer is not the same everywhere, and saying so is the point:
 *
 *   • Android and desktop Chrome/Edge get a real button. One tap opens the
 *     browser's own install dialog — no steps, no menu hunting.
 *   • iPhone gets three short steps instead, because Apple exposes no install
 *     API at all. A button there would be a lie.
 *   • Chrome or Firefox ON an iPhone gets neither: those browsers have no
 *     Add to Home Screen item, so the only useful thing to say is "open this
 *     in Safari".
 *
 * Hidden entirely once the app is installed, and for a month after someone
 * closes it — a colleague may still need telling, but not this person, and not
 * tomorrow.
 */

import CloseIcon from '@mui/icons-material/Close';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import IosShareIcon from '@mui/icons-material/IosShare';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { shouldOfferInstall } from '../lib/pwaInstall';
import { dismissInstallOffer, promptInstall, useInstallState } from '../services/pwaInstall';

export function InstallAppCard() {
  const { t } = useTranslation('common');
  const { mode, dismissed } = useInstallState();

  if (dismissed || !shouldOfferInstall(mode)) return null;

  return (
    <Alert
      icon={mode === 'prompt' ? <InstallMobileIcon /> : <IosShareIcon />}
      severity="info"
      variant="outlined"
      sx={{ mb: 2, alignItems: 'flex-start' }}
      action={
        <IconButton
          size="small"
          aria-label={t('install.dismiss', { defaultValue: 'Peida' })}
          onClick={dismissInstallOffer}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {t('install.title', { defaultValue: 'Võta RM24 telefoni' })}
      </Typography>

      {mode === 'prompt' && (
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body2">
            {t('install.promptBody', {
              defaultValue:
                'Ikoon avakuvale, avaneb täisekraanil ja töötab ka ilma võrguta.',
            })}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<InstallMobileIcon />}
            onClick={() => void promptInstall()}
          >
            {t('install.action', { defaultValue: 'Paigalda' })}
          </Button>
        </Stack>
      )}

      {mode === 'ios-safari' && (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t('install.iosBody', {
              defaultValue: 'iPhone’is teeb selle Safari ise — kolm sammu:',
            })}
          </Typography>
          <Typography component="ol" variant="body2" sx={{ m: 0, pl: 2.5 }}>
            <li>{t('install.ios1', { defaultValue: 'Vajuta all keskel jagamisnuppu' })}</li>
            <li>{t('install.ios2', { defaultValue: 'Vali „Lisa avakuvale“' })}</li>
            <li>{t('install.ios3', { defaultValue: 'Vajuta „Lisa“' })}</li>
          </Typography>
        </Box>
      )}

      {mode === 'ios-other' && (
        <Typography variant="body2">
          {t('install.iosOther', {
            defaultValue:
              'Ava see aadress Safaris — ainult sealt saab iPhone’is äppi avakuvale lisada.',
          })}
        </Typography>
      )}
    </Alert>
  );
}
